// frontend/src/components/common/ProtectedRoute.jsx
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { usePermission } from '../../hooks/usePermission';
import { Box, Typography, Button, CircularProgress, Container, Paper } from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';

/**
 * Composant pour protéger les routes en fonction des permissions
 * @param {Object} props - Propriétés du composant
 * @param {React.ReactNode} props.children - Composant enfant à afficher si l'accès est autorisé
 * @param {string} [props.permission] - Permission requise pour accéder à la route
 * @param {string} [props.role] - Rôle requis pour accéder à la route
 * @param {string} [props.redirectTo] - URL de redirection en cas d'accès non autorisé
 * @returns {React.ReactNode} - Composant enfant ou redirection
 */
const ProtectedRoute = ({ children, permission, role, redirectTo = '/login' }) => {
  const { t } = useTranslation('common');
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const location = useLocation();

  // Logs de débogage commentés pour éviter trop de sorties dans la console
  // console.log('ProtectedRoute - Auth state:', { 
  //   isAuthenticated, 
  //   authLoading, 
  //   user: user ? { ...user, isAdmin: user.isAdmin } : null 
  // });
  
  // Vérifier la permission si spécifiée
  const { hasPermission, loading: permissionLoading } = usePermission(permission);

  // Vérifier le rôle si spécifié
  const hasRequiredRole = role ? 
    (user?.roles?.includes(role) || 
     (role === 'admin' && user?.isAdmin) ||
     (role === 'owner' && user?.isOwner) ||
     (role === 'tenant' && user?.isTenant)) : 
    true;
  
  // Afficher un indicateur de chargement pendant la vérification
  if (authLoading || (permission && permissionLoading)) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <CircularProgress />
      </Box>
    );
  }
  
  // Si l'utilisateur n'est pas authentifié, rediriger vers la page de connexion
  if (!isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }
  
  // Si une permission est requise et que l'utilisateur ne l'a pas, afficher un message d'accès refusé
  if (permission && !hasPermission) {
    return (
      <Container component="main" maxWidth="sm" sx={{ py: 8 }}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
          <ErrorIcon color="error" sx={{ fontSize: 60, mb: 2 }} />
          <Typography variant="h4" color="error" gutterBottom>
            {t('auth.errors.accessDenied')}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {t('auth.errors.noPermission')}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => window.history.back()}
          >
            {t('common.back')}
          </Button>
        </Paper>
      </Container>
    );
  }
  
  // Si un rôle est requis et que l'utilisateur ne l'a pas, afficher un message d'accès refusé
  if (role && !hasRequiredRole) {
    return (
      <Container component="main" maxWidth="sm" sx={{ py: 8 }}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
          <ErrorIcon color="error" sx={{ fontSize: 60, mb: 2 }} />
          <Typography variant="h4" color="error" gutterBottom>
            {t('auth.errors.accessDenied')}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {t('auth.errors.noRole')}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => window.history.back()}
          >
            {t('common.back')}
          </Button>
        </Paper>
      </Container>
    );
  }
  
  // Si tout est OK, afficher le contenu protégé
  return children;
};

export default ProtectedRoute;